// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import API_URL from '../../config/api';
import { PageLayout, ActionButton } from '../../components/DesignSystem';
import { Button, message } from 'antd';
import { apiFetch } from '../../utils/apiFetch';

const CategoryView: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [category, setCategory] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => { fetchCategory(); }, [id, token]);

  const fetchCategory = async () => {
    try {
      if (!token) { navigate('/login'); return; }
      const res = await apiFetch(`${API_URL}/categories/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Not found');
      const data = await res.json();
      setCategory(data);
      const prodRes = await apiFetch(`${API_URL}/categories/${id}/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (prodRes.ok) setProducts(await prodRes.json());
    } catch {
      message.error('Failed to load category');
      navigate('/categories/list');
    } finally {
      setLoading(false);
    }
  };

  const archiveCategory = async () => {
    const reason = window.prompt(`Archive reason for "${category?.name}"?`);
    if (!reason) return;
    try {
      const res = await apiFetch(`${API_URL}/categories/${id}/archive`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (res.ok) { message.success('Category archived'); navigate('/categories/list'); }
      else { const e = await res.json(); message.error(e.message || 'Archive failed'); }
    } catch { message.error('Network error'); }
  };

  if (loading) return <p style={{ padding: 40, textAlign: 'center' }}>⏳ Loading...</p>;
  if (!category) return <p style={{ padding: 40, textAlign: 'center' }}>❌ Category not found</p>;

  const statusColor =
    category.status === 'Active'   ? '#52c41a' :
    category.status === 'Inactive' ? '#faad14' : '#8c8c8c';

  const imageUrl = category.image || '';
  const fmt = (v: any) => v ? new Date(v).toLocaleString('en-GB') : 'N/A';

  return (
    <PageLayout
      title={`🗂️ ${category.name}`}
      action={
        <div style={{ display: 'flex', gap: 10 }}>
          <ActionButton variant="edit" label="Edit" onClick={() => navigate(`/categories/edit/${id}`)} />
          <Button danger onClick={archiveCategory}>Archive</Button>
          <Button onClick={() => navigate('/categories/list')}>← Back</Button>
        </div>
      }
    >
      <div style={s.card}>
        {/* ── Banner ── */}
        <div style={{ ...s.banner, background: statusColor }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {imageUrl ? (
              <img src={imageUrl} alt={category.name} style={s.bannerAvatar} />
            ) : (
              <div style={s.bannerAvatarFallback}>🗂️</div>
            )}
            <div>
              <div style={{ color: '#fff', fontWeight: 700, fontSize: 20 }}>{category.name}</div>
              {category.description && (
                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, marginTop: 2 }}>{category.description}</div>
              )}
            </div>
          </div>
          <span style={s.statusPill}>{category.status || 'Active'}</span>
        </div>

        {/* ── Category Image ── */}
        {imageUrl && (
          <div style={s.section}>
            <h3 style={s.sectionTitle}>Image</h3>
            <img src={imageUrl} alt="category" style={{ width: 100, height: 100, borderRadius: 12, objectFit: 'cover', border: '2px solid #eee' }} />
          </div>
        )}

        {/* ── Details ── */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Category Details</h3>
          <div style={s.grid}>
            <Field label="Category Name" value={category.name} />
            <Field label="Status" value={category.status || 'Active'} highlight={statusColor} />
          </div>
          {category.description && (
            <div style={{ marginTop: 12 }}>
              <span style={s.fieldLabel}>Description</span>
              <div style={{ fontSize: 15, color: '#212529', marginTop: 4 }}>{category.description}</div>
            </div>
          )}
        </div>

        {/* ── Linked Products ── */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Products in this Category ({products.length})</h3>
          {products.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ textAlign: 'left', padding: 8 }}>Product</th>
                    <th style={{ padding: 8 }}>SKU</th>
                    <th style={{ padding: 8 }}>Stock</th>
                    <th style={{ padding: 8 }}>Price</th>
                    <th style={{ padding: 8 }}>Status</th>
                    <th style={{ padding: 8 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p: any) => (
                    <tr key={p.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: 8 }}>{p.name}</td>
                      <td style={{ padding: 8, textAlign: 'center' }}>{p.sku || '—'}</td>
                      <td style={{ padding: 8, textAlign: 'center' }}>{p.stock ?? 0}</td>
                      <td style={{ padding: 8, textAlign: 'center' }}>£{Number(p.price || 0).toFixed(2)}</td>
                      <td style={{ padding: 8, textAlign: 'center' }}>{p.status || 'Active'}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>
                        <Button size="small" type="link" onClick={() => navigate(`/products/view/${p.id}`)}>View</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <span style={{ fontSize: 13, color: '#888' }}>No products linked to this category yet.</span>
          )}
        </div>

        {/* ── Notes ── */}
        {category.notes && (
          <div style={s.section}>
            <h3 style={s.sectionTitle}>Notes</h3>
            <div style={s.notesBox}>{category.notes}</div>
          </div>
        )}

        {/* ── Record Info ── */}
        <div style={s.section}>
          <h3 style={s.sectionTitle}>Record Information</h3>
          <div style={s.grid}>
            <Field label="Category ID" value={category.id} />
            <Field label="Created"     value={fmt(category.created_at || category.createdAt)} />
            {(category.updated_at || category.updatedAt) && (
              <Field label="Last Updated" value={fmt(category.updated_at || category.updatedAt)} />
            )}
            {category.deleted_at && (
              <Field label="Archived On" value={fmt(category.deleted_at)} highlight="#ff4d4f" />
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

const Field = ({ label, value, highlight }: { label: string; value: any; highlight?: string }) => (
  <div style={{ marginBottom: 18 }}>
    <span style={{ display: 'block', fontSize: 11, color: '#6c757d', fontWeight: 700, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.6px' }}>
      {label}
    </span>
    <span style={{ fontSize: 15, color: highlight || '#212529', fontWeight: highlight ? 700 : 500 }}>
      {value || '—'}
    </span>
  </div>
);

const s: any = {
  card: { background: '#fff', borderRadius: 12, boxShadow: '0 4px 20px rgba(0,0,0,0.09)', overflow: 'hidden', marginBottom: 30 },
  banner: { padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 },
  bannerAvatar: { width: 56, height: 56, borderRadius: 10, objectFit: 'cover' as const, border: '2px solid rgba(255,255,255,0.4)' },
  bannerAvatarFallback: { width: 56, height: 56, borderRadius: 10, background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 28, flexShrink: 0 },
  statusPill: { display: 'inline-block', padding: '4px 16px', background: 'rgba(255,255,255,0.25)', borderRadius: 20, color: '#fff', fontWeight: 700, fontSize: 13 },
  section: { padding: '20px 24px', borderTop: '1px solid #eee' },
  sectionTitle: { marginTop: 0, marginBottom: 14, color: '#1a1a2e', fontSize: 16, fontWeight: 700, letterSpacing: '-0.2px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0 28px' },
  fieldLabel: { fontSize: 11, color: '#6c757d', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.6px' },
  notesBox: { background: '#f8f9fa', padding: '12px 16px', borderRadius: 6, fontSize: 14, color: '#495057', lineHeight: 1.6, whiteSpace: 'pre-wrap' as const },
};

export default CategoryView;
